/**
 * /datasets/:id — items of a version (`?v=`), the versions, and experiments that used it.
 * A live dataset shows its trace source instead of a CSV upload: its versions come from runs.
 * Archived, it is read-only: a banner with Restore, and no golden, new version or upload.
 */

import { useParams } from "react-router";
import { Box } from "@/components/ui/box";
import { Button, ButtonLink } from "@/components/ui/button";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { Pill } from "@/components/ui/pill";
import { Select } from "@/components/ui/select";
import { EmptyState, QueryState, errorText } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { ExperimentsTable } from "@/features/experiments/experiments-table";
import { useExperiments } from "@/features/experiments/hooks";
import { plural } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { useUiStore } from "@/stores/ui-store";
import { DatasetItems } from "./dataset-items";
import { NewVersionButton, OldVersionNotice, VersionsPanel } from "./dataset-versions";
import { useDataset, useUpdateDataset } from "./hooks";
import { SourceBox } from "./live/source-box";
import { isLive, versionPeriod } from "./live/version-period";
import type { Dataset } from "./types";
import { ProjectAction } from "@/features/projects/project-action";
import { ArchiveMenu } from "@/features/archive/archive-menu";
import { ArchivedBadge, ArchivedBanner } from "@/features/archive/archived";
import { useRestore } from "@/features/archive/hooks";
import { isArchived } from "@/features/archive/kinds";
import { useMayArchive } from "@/features/archive/may";

type Tab = "items" | "versions" | "experiments";

/** Golden is information only: a reviewed set people trust as the reference. */
function GoldenToggle({ dataset: d }: { dataset: Dataset }) {
  const update = useUpdateDataset(d.id);
  const notify = useUiStore((s) => s.notify);
  const toggle = () =>
    update.mutate(
      { is_golden: !d.is_golden },
      { onError: (e) => notify(errorText(e, "Could not update the dataset."), "error") },
    );
  return (
    <Button
      aria-pressed={d.is_golden}
      title="A reviewed set you trust as the reference. Information only."
      disabled={update.isPending}
      onClick={toggle}
    >
      {d.is_golden ? "Golden ✓" : "Mark as golden"}
    </Button>
  );
}

export function DatasetPage() {
  const { id = "" } = useParams();
  const dataset = useDataset(id);
  const experiments = useExperiments({ dataset_id: id });
  const url = useUrlState();
  const tab = (url.get("tab") ?? "items") as Tab;
  const d = dataset.data;
  const version = Number(url.get("v")) || d?.latest_version?.version || null;
  const latest = d?.latest_version?.version;
  const current = d?.versions.find((v) => v.version === version) ?? d?.latest_version ?? null;
  const live = !!d && isLive(d);
  const archived = isArchived(d);
  const mayArchive = useMayArchive("dataset")();
  const restore = useRestore("dataset");
  const usedBy = d?.used_by?.experiments ?? 0;
  const summary = current
    ? [`v${current.version}`, plural(current.item_count, "item"), versionPeriod(current)]
    : ["no items yet"];

  return (
    <QueryState query={dataset} what="the dataset">
      {d && (
        <>
          <div className="flex flex-col gap-2.5">
            <Crumbs items={[{ label: "Datasets", to: "/datasets" }, { label: d.name }]} />
            <PageHeader
              title={d.name}
              badges={
                <>
                  {d.is_golden && <Pill tone="gold">Golden</Pill>}
                  {live && <Pill tone="run">Live</Pill>}
                  {archived && <ArchivedBadge />}
                </>
              }
              subtitle={[...summary, d.description].filter(Boolean).join(" · ")}
              actions={
                <>
                  {!archived && (
                    <ProjectAction permission="datasets:write">
                      <GoldenToggle dataset={d} />
                    </ProjectAction>
                  )}
                  {d.versions.length > 0 && (
                    <Select
                      label="Version"
                      className="w-36"
                      value={String(version ?? "")}
                      onChange={(v) =>
                        url.set({
                          v: Number(v) === latest ? null : v,
                          page: null,
                          item: null,
                        })
                      }
                      options={d.versions.map((v) => ({
                        value: String(v.version),
                        label: `v${v.version}${v.version === latest ? " · latest" : ""}`,
                      }))}
                    />
                  )}
                  {!live && !archived && (
                    <ProjectAction permission="datasets:write">
                      <NewVersionButton datasetId={id} />
                    </ProjectAction>
                  )}
                  {!archived && <ArchiveMenu kind="dataset" row={d} size="md" />}
                </>
              }
            />
          </div>
          <ArchivedBanner kind="dataset" row={d} mayRestore={mayArchive} onRestore={() => void restore(d)}>
            {usedBy > 0 && `Still used by ${plural(usedBy, "past experiment")}.`}
          </ArchivedBanner>
          {live && <SourceBox datasetId={id} readOnly={archived} />}
          <Tabs
            label="Dataset sections"
            value={tab}
            onChange={(t) => url.set({ tab: t === "items" ? null : t })}
            items={[
              { key: "items", label: "Items", count: current?.item_count ?? 0 },
              { key: "versions", label: "Versions", count: d.versions.length },
              { key: "experiments", label: "Experiments", count: experiments.data?.length },
            ]}
          />
          {tab === "items" &&
            (current ? (
              <>
                {latest !== undefined && current.version !== latest && (
                  <OldVersionNotice
                    viewing={current.version}
                    latest={latest}
                    onLatest={() => url.set({ v: null, page: null, item: null })}
                  />
                )}
                <DatasetItems datasetId={id} version={current.version} />
              </>
            ) : live ? (
              <Box>
                <EmptyState
                  title="No traces pulled yet"
                  action={
                    !archived && (
                      <ProjectAction permission="experiments:write">
                        <ButtonLink variant="primary" to={`/experiments/new?dataset=${id}`}>
                          New experiment
                        </ButtonLink>
                      </ProjectAction>
                    )
                  }
                >
                  Traces are pulled when an experiment runs on this dataset. Each run saves the traces of its
                  period as a new version.
                </EmptyState>
              </Box>
            ) : (
              <Box>
                <EmptyState
                  title="No items yet"
                  action={
                    !archived && (
                      <ProjectAction permission="datasets:write">
                        <ButtonLink variant="primary" to={`/datasets/${id}/import`}>
                          Upload a CSV
                        </ButtonLink>
                      </ProjectAction>
                    )
                  }
                >
                  Upload a CSV of inputs and expected outputs. It becomes v1; each later upload saves a new
                  version.
                </EmptyState>
              </Box>
            ))}
          {tab === "versions" && (
            <VersionsPanel
              dataset={d}
              viewing={current?.version ?? null}
              onOpen={(v) => url.set({ tab: null, v: v === latest ? null : v, page: null, item: null })}
            />
          )}
          {tab === "experiments" && (
            <Box>
              <QueryState query={experiments} what="experiments">
                {experiments.data?.length ? (
                  <ExperimentsTable experiments={experiments.data} />
                ) : (
                  <EmptyState title="No experiment has used this dataset yet." />
                )}
              </QueryState>
            </Box>
          )}
        </>
      )}
    </QueryState>
  );
}
